import React, { useEffect, useState, lazy, Suspense } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    TextField,
    MenuItem,
    InputAdornment,
    Tooltip,
    Button,
    Typography,
    Box,
    IconButton,
    CircularProgress,
    Menu,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { toast } from 'react-toastify';
import { first, includes, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { fetchScormFiles, deleteScormFileApi, fetchFileApi } from '../../apis/scormUpload';
import StatusBadge from '../StatusBadge';
// import StartExtractionButton from '../Scorm/ScormUploads/StartExtractionButton';
import HistoryDialog from './HistoryDialog';
import UploadScormDialog from './UploadSCORMDialog';
import ConfirmationDialog from './ConfirmationDialog';
import { useDebounce } from '../../hooks/useDebounce';
import { useSubscription } from '../../hooks/useSubscription';
import { useOrganizationSettings } from '../../hooks/useOrganizationSettings';
import { extractErrorMessage, formatDate, formatTime, handleFileDownload } from '../../lib/utils';
import {
    Add,
    AllInclusive,
    Article,
    ArrowDropDown,
    DeleteForeverOutlined,
    FileDownloadOutlined,
    PictureAsPdf,
    VisibilityOutlined,
    Code,
} from '@mui/icons-material';
import PreviewDialog from '../subscription/PreviewDialog';
import { setHasCompletedExtraction } from '../../store/slices/scormFilesSlice';
import FileStatusListener from './FileStatusListener';
import { ReactComponent as ClaudeIcon } from '../../assets/icons/claude.svg';
import TablePagination from '../TablePagination';

const ContinuousLearningDialog = lazy(() => import('../AI-Integration/Dialogs/ContinuousLearning'));

const TeamsAppSettingsDialog = lazy(() =>
    import('../AI-Integration/Dialogs/TeamsAppSettings').then((module) => ({
        default: module.TeamsAppSettingsDialog,
    })),
);

// Loading fallback for lazy loaded component
const DialogLoader = () => (
    <Box className="dialog-loader">
        <CircularProgress size={24} />
    </Box>
);

const formatSize = (size) => (size ? `${parseFloat(size).toFixed(2)} MB` : '-');

async function fetchScormFilesList(page = 1, limit = 5, search, timeFilter, sortModel) {
    try {
        const response = await fetchScormFiles(page, limit, search, timeFilter, sortModel);
        const scormFiles = response?.data?.results || [];
        return { files: scormFiles, count: response?.data?.count || 0 };
    } catch (err) {
        // console.error('Error fetching page', page, err);
    }
}

const DashboardSCORMFiles = ({ pageSize = 5 }) => {
    const dispatch = useDispatch();

    const { canUploadCourse, isCoursesExhausted, isStorageExhausted, coursesRemaining, storageRemaining } =
        useSubscription();
    const { refetch } = useOrganizationSettings();

    const permissions = useSelector((state) => state.auth.permissions);
    const subscription = useSelector((state) => state.auth.subscription);
    const user = useSelector((state) => state.auth.user);

    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [timeFilter, setTimeFilter] = useState('all');
    const [openUpload, setOpenUpload] = useState(false);
    const [previewDialog, setPreviewDialog] = useState(false);
    const [previewContent, setPreviewContent] = useState({ filename: null, data: null });
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        pageSize,
        page: 0,
    });
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        type: 'warning',
        title: '',
        message: '',
        onConfirm: null,
        loading: false,
    });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedFile, _setSelectedFile] = useState(null);
    const [sortModel, setSortModel] = React.useState(null);
    const [teamsAppSettingsOpen, setTeamsAppSettingsOpen] = useState(false);
    const [selectedFileForContinuousLearning, setSelectedFileForContinuousLearning] = useState(null);
    const [loadingFileId, setLoadingFileId] = useState(null);
    const [continuousLearningOpen, setContinuousLearningOpen] = useState(false);
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
    const [selectedFileForDownload, setSelectedFileForDownload] = useState(null);

    const debounceSearch = useDebounce(search);
    const isTrial = subscription?.name?.toLowerCase() === 'trial';

    useEffect(() => {
        getScormFilesHandler();
    }, [paginationModel, debounceSearch, timeFilter, sortModel]);

    const getScormFilesHandler = async () => {
        if (!isPermitted('list')) return;

        setLoading(true);
        try {
            const data = await fetchScormFilesList(
                paginationModel.page + 1,
                paginationModel.pageSize,
                search,
                timeFilter,
                sortModel,
            );
            setFiles(data?.files ?? []);
            setTotalCount(data?.count ?? 0);

            // Check for completed extractions and update Redux store (only needed for non-trial users)
            // Trial users already see chatbot via isTrial condition, so skip the check for them
            if (isTrial) {
                const hasCompleted = (data?.files ?? []).some((file) => file?.last_extraction?.status === 'completed');
                dispatch(setHasCompletedExtraction(Boolean(hasCompleted)));
            }
        } catch {
            toast.error('Failed to load files');
            setFiles([]);
            setTotalCount(0);

            // Only reset extraction status check for non-trial users
            if (isTrial) dispatch(setHasCompletedExtraction(false));
        } finally {
            setLoading(false);
        }
    };

    const handleJobStatusUpdate = (jobStatus) => {
        if (!jobStatus || jobStatus.jobId == null) {
            return;
        }

        const jobId = Number(jobStatus.jobId);

        // jobId corresponds to scorm_files.id (the file ID)
        setFiles((prevFiles) => {
            const fileIndex = prevFiles.findIndex((file) => Number(file?.id) === jobId);

            if (fileIndex !== -1) {
                const updatedFiles = [...prevFiles];
                const file = updatedFiles[fileIndex];
                const nextLastExtraction = file.last_extraction
                    ? {
                          ...file.last_extraction,
                          status: jobStatus?.status ?? file.last_extraction.status,
                          ...(jobStatus.download_url
                              ? { download_link: jobStatus.download_url }
                              : {}),
                      }
                    : jobStatus.download_url
                      ? {
                            status: jobStatus?.status,
                            download_link: jobStatus.download_url,
                        }
                      : file.last_extraction;

                updatedFiles[fileIndex] = {
                    ...file,
                    status: jobStatus?.status,
                    last_extraction: nextLastExtraction,
                };

                if (isTrial && jobStatus?.status === 'completed') {
                    dispatch(setHasCompletedExtraction(true));
                }

                return updatedFiles;
            }

            // If file doesn't exist in current view, refresh the list
            getScormFilesHandler();
            return prevFiles;
        });
    };

    const handleDelete = (fileId) => {
        setConfirmDialog({
            open: true,
            type: 'danger',
            title: 'Delete File',
            message: 'Are you sure you want to delete this file permanently?',
            onConfirm: () => deleteFileHandler(fileId),
        });
    };

    const deleteFileHandler = async (fileId) => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
            await deleteScormFileApi(fileId);
            toast.success('File deleted successfully');
            getScormFilesHandler();
            setConfirmDialog({
                open: false,
                type: 'warning',
                title: '',
                message: '',
                onConfirm: null,
                loading: false,
            });
        } catch (err) {
            toast.error('Failed to delete file: ' + err);
            setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
    };

    const getFilePreviewHandler = async (file) => {
        try {
            const response = await fetchFileApi(file?.last_extraction?.file_id);
            setPreviewContent({ filename: response?.data?.file_name, data: response?.data?.preview_content });
            setPreviewDialog(true);
        } catch (err) {
            toast.error('Failed to load file preview');
        }
    };

    const handlePreviewDialogClose = () => {
        setPreviewContent({ filename: null, data: null });
        setPreviewDialog(false);
    };

    const handleCloseDialog = () => setDialogOpen(false);

    const isPermitted = (value) => includes(permissions?.scorm, value);

    const handleSortModelChange = async (sortOption) => {
        if (isEmpty(sortOption)) {
            setSortModel(null);
            return;
        }

        const { field, sort } = first(sortOption);
        setSortModel({ field, sort });
    };

    const handlePdfDownload = (link) => {
        if (isEmpty(link)) {
            toast.warning('Download link not available');
            return;
        }

        // Create a temporary anchor element to download the file
        const anchor = document.createElement('a');
        anchor.href = link;
        anchor.download = ''; // Let the browser determine the filename
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    };

    const handleMarkdownDownload = (link) => {
        if (isEmpty(link)) {
            toast.warning('Download link not available');
            return;
        }

        // Create a temporary anchor element to download the file
        const anchor = document.createElement('a');
        anchor.href = link;
        anchor.download = ''; // Let the browser determine the filename
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    };

    const handleDownloadMenuOpen = (event, file) => {
        setDownloadMenuAnchor(event.currentTarget);
        setSelectedFileForDownload(file);
    };

    const handleDownloadMenuClose = () => {
        setDownloadMenuAnchor(null);
        setSelectedFileForDownload(null);
    };

    const handleDownloadOption = (type) => {
        const file = selectedFileForDownload;
        if (!file) return;

        switch (type) {
            case 'json':
                if (file?.last_extraction?.download_link) {
                    handleFileDownload(file.last_extraction.download_link);
                }
                break;
            case 'pdf':
                if (file?.last_extraction?.download_pdf_link) {
                    handlePdfDownload(file.last_extraction.download_pdf_link);
                }
                break;
            case 'markdown':
                if (file?.last_extraction?.download_markdown_link) {
                    handleMarkdownDownload(file.last_extraction.download_markdown_link);
                }
                break;
            case 'skills':
                if (file?.last_extraction?.download_skills_link) {
                    handleMarkdownDownload(file.last_extraction.download_skills_link);
                }
                break;
            default:
                break;
        }
        handleDownloadMenuClose();
    };

    const handleAddToContinuousLearning = async (file, canContinuousLearning) => {
        if (!canContinuousLearning) return;

        setLoadingFileId(file?.id);
        try {
            // Check if settings exist, if not fetch them first
            setSelectedFileForContinuousLearning(file);
            const settings = await refetch();
            const hasSettings = settings?.has_openai_api_key && settings?.teams_tenant_id;

            if (!hasSettings) {
                // Open TeamsAppSettings modal instead of navigating
                toast.info('Configure your settings to proceed with Continuous Learning.');
                setTeamsAppSettingsOpen(true);
                return;
            }
            setContinuousLearningOpen(true);
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to check settings'));
        } finally {
            setLoadingFileId(null);
        }
    };

    const columns = [
        {
            field: 'file_name',
            headerName: 'File Name',
            flex: 2.5,
            renderCell: (params) => <Tooltip title={params?.value}>{params?.value}</Tooltip> || '-',
        },
        {
            field: 'title',
            headerName: 'Course Name',
            flex: 2.5,
            renderCell: (params) => <Tooltip title={params?.value}>{params?.value}</Tooltip> || '-',
        },
        {
            field: 'file_size',
            headerName: 'Size',
            flex: 0.7,
            valueFormatter: formatSize,
        },
        {
            field: 'created_at',
            headerName: 'Date',
            flex: 1,
            valueFormatter: formatDate,
        },
        {
            field: 'last_extraction_started_at',
            headerName: 'Start Time',
            flex: 0.7,
            renderCell: (params) => formatTime(params?.row?.last_extraction?.started_at) || '-',
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => {
                const remark =
                    params?.row?.status === 'failed' ? params?.row?.remark : params?.row?.last_extraction?.remarks;
                const statusBadge = (
                    <StatusBadge
                        status={params?.row?.last_extraction?.status ?? params.row?.status}
                        type={params?.row?.last_extraction === null ? 'file' : 'extraction'}
                    />
                );

                return remark &&
                    (params.row.status === 'failed' ||
                        params.row.status === 'rejected' ||
                        params?.row?.last_extraction?.status === 'failed') ? (
                    <Tooltip title={remark} arrow placement="top">
                        <span>{statusBadge}</span>
                    </Tooltip>
                ) : (
                    statusBadge
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1.5,
            sortable: false,
            renderCell: (params) => {
                const file = params?.row;
                if (!file) return null;

                const extractionStatus = file?.last_extraction == null ? params?.value : file?.last_extraction?.status;
                const isFailed = extractionStatus === 'failed';
                const canDownload = !isEmpty(file?.last_extraction?.download_link);
                const isExtractionCompleted = file?.last_extraction?.status === 'completed';
                const canPreview = isExtractionCompleted && !isFailed && file?.status === 'completed';
                const canDownloadFile = canDownload && !isFailed && file?.status === 'completed' && !isTrial;
                const isSubscriptionExpired = subscription?.end_date ? new Date(subscription.end_date) < new Date() : false;

                const canContinuousLearning = canPreview && isEmpty(file?.associated_teams_app);

                return (
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >

                        {/* View */}
                        {isPermitted('view') ? (
                            <Tooltip title="View Files" placement="top" arrow>
                                <IconButton
                                    onClick={() => canPreview && getFilePreviewHandler(file)}
                                    disabled={!canPreview}
                                    sx={{
                                        color: canPreview ? '#4C43A3' : '#4C43A366',
                                        cursor: canPreview ? 'pointer' : 'not-allowed',
                                    }}
                                >
                                    <VisibilityOutlined fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                        ) : null}

                        {isPermitted('add') && !isTrial ? (
                            <>
                                {/* Extract */}
                                {/* <StartExtractionButton file={file} /> */}

                                {/* Download Dropdown */}
                                {(() => {
                                    const hasJsonDownload = canDownloadFile;
                                    const hasPdfDownload = !isEmpty(file?.last_extraction?.download_pdf_link);
                                    const hasMarkdownDownload = !isEmpty(file?.last_extraction?.download_markdown_link);
                                    const hasClaudeSkillsDownload = !isEmpty(file?.last_extraction?.download_skills_link);
                                    const hasAnyDownload = hasJsonDownload || hasPdfDownload || hasMarkdownDownload;

                                    return (
                                        <>
                                            <Tooltip title={hasAnyDownload ? 'Download' : ''} placement="top" arrow>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        position: 'relative',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={(e) => hasAnyDownload && handleDownloadMenuOpen(e, file)}
                                                        disabled={!hasAnyDownload}
                                                        style={{
                                                            color: hasAnyDownload ? '#4C43A3' : '#4C43A366',
                                                            cursor: hasAnyDownload ? 'pointer' : 'not-allowed',
                                                        }}
                                                    >
                                                        <FileDownloadOutlined fontSize="medium" />
                                                    </IconButton>
                                                    {hasAnyDownload && (
                                                        <ArrowDropDown
                                                            fontSize="small"
                                                            style={{
                                                                position: 'absolute',
                                                                right: '4px',
                                                                bottom: '4px',
                                                                fontSize: '12px',
                                                                pointerEvents: 'none',
                                                                color: hasAnyDownload ? '#4C43A3' : '#4C43A366',
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </Tooltip>
                                            <Menu
                                                anchorEl={downloadMenuAnchor}
                                                open={
                                                    Boolean(downloadMenuAnchor) && selectedFileForDownload?.id === file?.id
                                                }
                                                onClose={handleDownloadMenuClose}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'right',
                                                }}
                                            >
                                                {hasJsonDownload && (
                                                    <MenuItem onClick={() => handleDownloadOption('json')}>
                                                        <ListItemIcon>
                                                            <Code fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText>JSON (.json)</ListItemText>
                                                    </MenuItem>
                                                )}
                                                {hasPdfDownload && (
                                                    <MenuItem onClick={() => handleDownloadOption('pdf')}>
                                                        <ListItemIcon>
                                                            <PictureAsPdf fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText>PDF (.pdf)</ListItemText>
                                                    </MenuItem>
                                                )}
                                                {hasMarkdownDownload && (
                                                    <MenuItem onClick={() => handleDownloadOption('markdown')}>
                                                        <ListItemIcon>
                                                            <Article fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText>Markdown (.md)</ListItemText>
                                                    </MenuItem>
                                                )}
                                                {hasClaudeSkillsDownload && (
                                                    <MenuItem onClick={() => handleDownloadOption('skills')}>
                                                        <ListItemIcon>
                                                            <ClaudeIcon style={{ fontSize: '1.25rem' }} />
                                                        </ListItemIcon>
                                                        <ListItemText>Claude Skills (.zip)</ListItemText>
                                                    </MenuItem>
                                                )}
                                            </Menu>
                                        </>
                                    );
                                })()}
                            </>
                        ) : null}

                        {/* Delete */}
                        {isPermitted('delete') && !isTrial ? (
                            <Tooltip title="Delete" placement="top" arrow>
                                <IconButton onClick={() => handleDelete(file?.id)} color="error">
                                    <DeleteForeverOutlined />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </Box>
                );
            },
        },
    ];

    // Get orgId from user - must be organization_id
    const orgId = user?.organization;

    return (
        <>
            {orgId && <FileStatusListener orgId={orgId} onJobStatusUpdate={handleJobStatusUpdate} />}
            <div className="custom_table_container">
                <Box variant="div" className="table_filters">
                    <Typography variant="h6" fontWeight={600}>
                        Courses
                    </Typography>
                    <Box className="filter_box">
                        <TextField
                            size="small"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <span className="material-symbols-rounded">search</span>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <TextField
                            select
                            label="Filter by Date"
                            size="small"
                            value={timeFilter}
                            onChange={(e) => setTimeFilter(e.target.value)}
                            style={{ minWidth: 180 }}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="this_week">This Week</MenuItem>
                            <MenuItem value="this_month">This Month</MenuItem>
                            <MenuItem value="this_year">This Year</MenuItem>
                        </TextField>
                        {isPermitted('add') ? (
                            <Tooltip
                                title={
                                    !canUploadCourse
                                        ? isCoursesExhausted
                                            ? `Course quota exhausted. ${coursesRemaining} courses remaining.`
                                            : isStorageExhausted
                                              ? `Storage quota exhausted. ${storageRemaining} MB remaining.`
                                              : 'Subscription inactive'
                                        : ''
                                }
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        startIcon={<Add />}
                                        onClick={() => setOpenUpload(true)}
                                        disabled={!canUploadCourse}
                                    >
                                        Upload Course
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box variant="div" className="custom_table_responsive">
                    <DataGrid
                        rows={files}
                        columns={columns}
                        getRowId={(row) => row.id}
                        autoHeight
                        pageSizeOptions={[5, 10, 25, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pagination
                        paginationMode="server"
                        loading={loading}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        localeText={{
                            noRowsLabel: 'No Uploaded Courses Found',
                            sortLabel: '',
                        }}
                        rowCount={totalCount}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                        slots={{
                            pagination: () => (
                                <TablePagination
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    totalCount={totalCount}
                                    pageSizeOptions={[5, 10, 25, 50]}
                                />
                            ),
                        }}
                    />
                    <UploadScormDialog
                        openUpload={openUpload}
                        onClose={() => setOpenUpload(false)}
                        onUploadSuccess={() => getScormFilesHandler()}
                    />
                </Box>
            </div>

            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={() =>
                    setConfirmDialog({
                        open: false,
                        type: 'warning',
                        title: '',
                        message: '',
                        onConfirm: null,
                        loading: false,
                    })
                }
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                type={confirmDialog.type}
                loading={confirmDialog.loading}
            />

            <HistoryDialog open={dialogOpen} onClose={handleCloseDialog} scormId={selectedFile?.id} />

            <PreviewDialog
                open={previewDialog}
                onClose={handlePreviewDialogClose}
                data={previewContent.data}
                fileName={previewContent?.filename}
            />

            <Suspense fallback={<DialogLoader />}>
                <TeamsAppSettingsDialog
                    open={teamsAppSettingsOpen}
                    onClose={() => {
                        setTeamsAppSettingsOpen(false);
                        setSelectedFileForContinuousLearning(null);
                        setLoadingFileId(null);
                    }}
                    onSuccess={async () => {
                        // After settings are saved, refetch and open continuous learning modal
                        const settings = await refetch();
                        if (settings?.has_openai_api_key && settings?.teams_tenant_id && selectedFileForContinuousLearning)
                            setContinuousLearningOpen(true);
                        setTeamsAppSettingsOpen(false);
                        setLoadingFileId(null);
                    }}
                />
            </Suspense>

            <Suspense fallback={<DialogLoader />}>
                <ContinuousLearningDialog
                    open={continuousLearningOpen}
                    onClose={() => setContinuousLearningOpen(false)}
                    file={selectedFileForContinuousLearning}
                />
            </Suspense>
        </>
    );
};

export default DashboardSCORMFiles;
